'use strict';/*jslint ts:true, react:true*/
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Editor} from '../../../shared/editor/editor';
import {use_screen} from '../lib/screen_util';
import {Button} from '../../../shared/button';
import {use_download} from '../shared/use_download';
import styles from './game_boost_works_with_popular_games.module.less';
import cn from 'classnames';

const scroll_speed = 0.5;
const color_options = ['blue', 'pink', 'orange', 'green', 'yellow']
    .map(c=>({value: c, label: c}));

interface i_chip {
    name: string;
    color: string;
}

interface i_row {
    chips: i_chip[];
}

interface i_game_boost_works_with_popular_games_props {
    title: string;
    button_text: string;
    download_type?: string;
    rows: i_row[];
}

export const Game_boost_works_with_popular_games = (
props: i_game_boost_works_with_popular_games_props)=>{
    const {title, button_text, download_type = 'gameboost'} = props;
    const rows = props.rows||[];
    const {screen} = use_screen();
    const is_mobile = screen=='xs' || screen=='sm';
    const {download, download_gameboost} = use_download();
    const wrapper_ref = useRef<HTMLDivElement>(null);
    const row_refs = useRef<(HTMLDivElement|null)[]>([]);
    const [copies, set_copies] = useState(3);
    useEffect(()=>{
        let raf = 0;
        const update = ()=>{
            raf = 0;
            const wrapper = wrapper_ref.current;
            if (!wrapper)
                return;
            const shift = -wrapper.getBoundingClientRect().top*scroll_speed;
            let needed = 2;
            row_refs.current.forEach((row, i)=>{
                const set = row?.firstElementChild as HTMLElement|null;
                const w = set?.offsetWidth;
                if (!row || !w)
                    return;
                needed = Math.max(needed,
                    Math.ceil(window.innerWidth/w)+1);
                // the set repeats every w px, so wrap the offset into [-w, 0)
                const x = shift*(i%2 ? -1 : 1)%w+w%w-w;
                row.style.transform = `translate3d(${x}px, 0, 0)`;
            });
            set_copies(needed);
        };
        const on_scroll = ()=>{
            if (!raf)
                raf = requestAnimationFrame(update);
        };
        update();
        window.addEventListener('scroll', on_scroll, {passive: true});
        window.addEventListener('resize', on_scroll);
        return ()=>{
            window.removeEventListener('scroll', on_scroll);
            window.removeEventListener('resize', on_scroll);
            if (raf)
                cancelAnimationFrame(raf);
        };
    }, [props.rows]);
    const on_download = ()=>{
        if (download_type=='gameboost')
            return void download_gameboost();
        download();
    };
    return <div className={styles.wrapper} ref={wrapper_ref}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.rows}>
        {rows.map((row, i)=>
          <div key={i} className={styles.row}
            ref={el=>{ row_refs.current[i] = el; }}>
            {Array.from({length: copies}, (_, copy)=>
              <div key={copy} className={styles.set}
                aria-hidden={copy>0 || undefined}>
                {(row.chips||[]).map((chip, j)=>
                  <div key={j} className={cn(styles.chip,
                    styles[chip.color]||styles.blue)}>{chip.name}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.cta}>
        <Button on_click={on_download} caption={button_text}
          color="black2023"
          size={is_mobile ? 'md' : 'lg'}
          round />
      </div>
    </div>;
};

interface i_game_boost_works_with_popular_games_editor_props {
    value: i_game_boost_works_with_popular_games_props;
    on_change: (new_val: i_game_boost_works_with_popular_games_props)=>void;
}

export const Game_boost_works_with_popular_games_editor = (
{value, on_change}: i_game_boost_works_with_popular_games_editor_props)=>{
    const rows = value.rows||[];
    const set_rows = (next: i_row[])=>on_change({...value, rows: next});
    const set_chips = (row_i: number, chips: i_chip[])=>
        set_rows(rows.map((r, i)=>i==row_i ? {...r, chips} : r));
    return <Editor value={value} on_change={on_change}
      editors={[
        {key: 'title', label: 'Title', type: 'text'},
        {key: 'button_text', label: 'Button text', type: 'text'},
        {key: 'download_type', label: 'Download type (gameboost / vpn)',
          type: 'text'},
      ]}>
      <Fragment>
        <h4>Rows (direction alternates: right, left, right...)</h4>
        {rows.map((row, row_i)=><div key={row_i}>
          <h5>Row {row_i+1}</h5>
          {(row.chips||[]).map((chip, chip_i)=>
            <Editor<i_chip> key={chip_i}
              title={chip.name||`Chip ${chip_i+1}`}
              value={chip}
              editors={[
                {key: 'name', label: 'Name', type: 'text'},
                {key: 'color', label: 'Color', type: 'selector',
                  options: color_options},
              ]}
              on_change={v=>set_chips(row_i, row.chips.map((c, i)=>
                i==chip_i ? v : c))}
              on_remove={()=>set_chips(row_i,
                row.chips.filter((_, i)=>i!=chip_i))} />
          )}
          <Button caption="Add chip" size="xs"
            on_click={()=>set_chips(row_i,
              [...row.chips||[], {name: 'Game', color: 'blue'}])} />
          <Button caption="Delete row" size="xs" color="secondary"
            on_click={()=>set_rows(rows.filter((_, i)=>i!=row_i))} />
        </div>)}
        <Button caption="Add row" size="xs"
          on_click={()=>set_rows([...rows, {chips: []}])} />
      </Fragment>
    </Editor>;
};
